import { captureError } from '@/config/sentry';
import { askPermissionAndGetFcmToken, getFcmTokenIfGranted } from '@/lib/firebase';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import { getAppSW } from '@/register-sw';
import { useCallback, useEffect, useState } from 'react';
import { registerDeviceToken } from '../api/registerDeviceToken';

export const FCM_TOKEN_STORAGE_KEY = 'fcmToken';
export const DID_GRANT_PERMISSION_STORAGE_KEY = 'didGrantNotificationsPermission';

export function usePushNotifications() {
  const [permission, setPermission] = useState<NotificationPermission>('Notification' in globalThis ? Notification.permission : 'denied');
  const [token, setToken] = useState<string | null>(localStorage.getItem(FCM_TOKEN_STORAGE_KEY));
  const [isLoading, setIsLoading] = useState(false);

  // If already granted, try to sync token silently
  useEffect(() => {
    void (async () => {
      if (permission !== 'granted' || token) return;
      const sw = await getAppSW();
      if (!sw) return;
      try {
        const t = await getFcmTokenIfGranted(sw);
        if (t && t !== localStorage.getItem(FCM_TOKEN_STORAGE_KEY)) {
          await registerDeviceToken({ tokenId: t });
          localStorage.setItem(FCM_TOKEN_STORAGE_KEY, t);
          setToken(t);
        }
      } catch (error) {
        captureError(error);
      }
    })();
  }, [permission, token]);

  const enable = useCallback(async () => {
    setIsLoading(true);
    try {
      const sw = await getAppSW();
      if (!sw) throw new Error('Service worker not ready');
      const t = await askPermissionAndGetFcmToken(sw);
      setPermission('Notification' in globalThis ? Notification.permission : 'denied');
      if (!t) return;

      const storedToken = localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
      const didGrantPermission = localStorage.getItem(DID_GRANT_PERMISSION_STORAGE_KEY);

      if (t !== storedToken) {
        await registerDeviceToken({ tokenId: t });
        localStorage.setItem(FCM_TOKEN_STORAGE_KEY, t);
      }

      if (didGrantPermission !== 'true') {
        localStorage.setItem(DID_GRANT_PERMISSION_STORAGE_KEY, 'true');
      }

      void captureAnalyticsEvent('notifications_push_enabled');
      setToken(t);
    } catch (error) {
      captureError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { permission, token, isLoading, enable } as const;
}
